import { useCallback, useEffect, useState } from "react";
import { getRequest } from "./ApiCalls";

export interface UseTableDataOptions {
  endpoint: string;
  initialFilters?: Record<string, any>;
  isFormData?: boolean;
  skip?: boolean;
}

export interface UseTableDataResult<T> {
  dataSource: T[];
  dataSize: number;
  loading: boolean;
  filterConfig: Record<string, any>;
  setFilterConfig: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  refetch: () => Promise<void>;
  handleChangePage: (page: number) => void;
  handlePageSizeChange: (size: number) => void;
  fetchExport: (
    overrides?: Record<string, any>
  ) => Promise<{ success: boolean; data: T[] }>;
}

/**
 * Shared list-page data hook.
 *
 * Encapsulates the fetch -> dataSource/dataSize -> filterConfig -> pagination
 * pattern that was previously hand-rolled in every list page (Customer,
 * PurchaseRequest, PurchaseOrder, PaymentRequest, Discount, BorrowedCash,
 * AdvancePayment, User, EndOfDay, CreditBillingStatement,
 * CreditBillingPayment, PettyCashEntry, PettyCashRequisition, the Reports pages,
 * etc). Pages keep their own columns, modals, and mutation (create / update /
 * delete) logic — this hook only owns "give me a page of rows for the
 * current filters".
 *
 * fetchData is memoized with useCallback so the effect dependency is
 * correct and complete (no react-hooks/exhaustive-deps suppression needed).
 */
export function useTableData<T = any>({
  endpoint,
  initialFilters = {},
  isFormData = false,
  skip = false,
}: UseTableDataOptions): UseTableDataResult<T> {
  const [dataSource, setDataSource] = useState<T[]>([]);
  const [dataSize, setDataSize] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterConfig, setFilterConfig] = useState<Record<string, any>>({
    page: 1,
    ...initialFilters,
  });

  const fetchData = useCallback(async () => {
    if (skip) return;
    setLoading(true);
    setDataSource([]);
    const response = await getRequest(endpoint, filterConfig, isFormData);
    if (response.success) {
      setDataSize(response.data?.size ?? 0);
      setDataSource(response.data?.data ?? []);
    }
    setLoading(false);
  }, [endpoint, filterConfig, isFormData, skip]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChangePage = (page: number) => {
    setFilterConfig((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (size: number) => {
    setFilterConfig((prev) => ({ ...prev, page: 1, page_size: size }));
  };

  const fetchExport = useCallback(
    async (overrides: Record<string, any> = {}) => {
      const response = await getRequest(
        endpoint,
        { ...filterConfig, page: "", page_size: "", ...overrides },
        isFormData
      );
      return {
        success: response.success,
        data: response.success ? response.data?.data ?? [] : [],
      };
    },
    [endpoint, filterConfig, isFormData]
  );

  return {
    dataSource,
    dataSize,
    loading,
    filterConfig,
    setFilterConfig,
    refetch: fetchData,
    handleChangePage,
    handlePageSizeChange,
    fetchExport,
  };
}
